import React, { useEffect, useState, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { X, Sparkles, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  useOnboardingSafe,
  FEATURE_TOOLTIP_STEPS,
  TooltipPage,
} from './OnboardingContext';
import { DEMO_VIDEO_ID } from '../../constants/demoVideo';
import { RootState } from '../../reducers';

// Tooltip messages for each feature - step-aware
// Some features have multiple steps with different messages
const TOOLTIP_MESSAGES: Record<
  string,
  Array<{ title: string; description: string; buttonText?: string }>
> = {
  'ai-summary': [
    {
      title: 'AI-Generated Summaries',
      description:
        'Check out the demo video to see how ScreenApp creates AI summaries with chapters and key points!',
      buttonText: 'Watch Demo',
    },
    {
      title: 'Your AI Summary',
      description:
        'Click the Summary tab to view your AI-generated summary with chapters and key points!',
      buttonText: 'Got it!',
    },
  ],
  transcript: [
    {
      title: 'Searchable Transcripts',
      description:
        'Check out the demo video to see how ScreenApp transcribes your recordings with speaker detection!',
      buttonText: 'Watch Demo',
    },
    {
      title: 'View Transcript',
      description:
        "Click the Transcript tab to view and search through your recording's full transcription!",
      buttonText: 'Got it!',
    },
  ],
  notes: [
    {
      title: 'Automatic Note-Taking',
      description:
        'Record your voice and let AI transcribe it instantly. Perfect for quick notes and memos!',
    },
  ],
  'ai-chat': [
    {
      title: 'Chat with Your Recordings',
      description:
        'Click here to open the AI chat panel and ask questions about any of your recordings!',
    },
  ],
  'youtube-summary': [
    {
      title: 'Import YouTube Videos',
      description:
        'Paste any YouTube URL here to import it and get an instant AI summary and transcript!',
    },
  ],
  all: [
    {
      title: 'Get Started with the Demo',
      description:
        "Check out the demo video to see summaries, transcripts, and all of ScreenApp's powerful features in action!",
    },
  ],
};

// Fallback positions for features (when element not found)
const FALLBACK_POSITIONS: Record<string, { top: number; left: number }> = {
  'ai-summary': { top: 200, left: 100 },
  transcript: { top: 300, left: 100 },
  notes: { top: 400, left: 100 },
  'ai-chat': { top: 100, left: 300 },
  'youtube-summary': { top: 200, left: 300 },
  all: { top: 200, left: 200 },
};

// Helper to determine current page type from location
const getCurrentPage = (pathname: string, hash: string): TooltipPage => {
  const path = hash ? hash.replace('#', '') : pathname;
  // Check if we're on a video page (library/teamId/folderId/fileId pattern)
  const parts = path.split('/').filter(Boolean);
  if (parts[0] === 'library' && parts.length === 4) {
    return 'video';
  }
  return 'dashboard';
};

interface TooltipPosition {
  top: number;
  left: number;
  arrowPosition: 'top' | 'bottom' | 'left' | 'right';
}

const FeatureTooltip: React.FC = () => {
  const onboarding = useOnboardingSafe();
  const location = useLocation();
  const navigate = useNavigate();
  const [position, setPosition] = useState<TooltipPosition | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  const hasNavigatedRef = useRef(false);
  const lastTooltipStepRef = useRef<number>(-1);
  const tooltipRef = useRef<HTMLDivElement>(null);

  // Get user's selected team for navigation
  const selectedTeam = useSelector(
    (state: RootState) => state.team.selectedTeam,
  );
  const folders = useSelector((state: RootState) => state.library.folders.data);

  // Extract values safely (hooks must be called unconditionally)
  const showTooltips = onboarding?.showTooltips ?? false;
  const activeTooltipFeature = onboarding?.activeTooltipFeature ?? null;
  const tooltipStep = onboarding?.tooltipStep ?? 0;
  const advanceTooltip = onboarding?.advanceTooltip;
  const cancelTooltip = onboarding?.cancelTooltip;
  const isObTooltipsVariant = onboarding?.isObTooltipsVariant ?? false;

  // Determine current page
  const currentPage = getCurrentPage(location.pathname, location.hash);

  // Get feature steps config
  const featureSteps = activeTooltipFeature
    ? FEATURE_TOOLTIP_STEPS[activeTooltipFeature] || []
    : [];

  // Get current step config
  const currentStepConfig = featureSteps[tooltipStep] || null;

  // Check if we're on the correct page for this tooltip step
  const isOnCorrectPage = currentStepConfig?.page === currentPage;

  // Get message for current step
  const messages = activeTooltipFeature
    ? TOOLTIP_MESSAGES[activeTooltipFeature] || []
    : [];
  const message = messages[tooltipStep] || {
    title: 'Discover This Feature',
    description: 'Click here to explore this powerful feature!',
  };

  // Navigate to demo video when advancing to step 2 for multi-step features
  useEffect(() => {
    // Reset navigation flag when tooltip step changes
    if (tooltipStep !== lastTooltipStepRef.current) {
      hasNavigatedRef.current = false;
      lastTooltipStepRef.current = tooltipStep;
    }

    const teamId = selectedTeam?.teamId;
    if (
      showTooltips &&
      activeTooltipFeature &&
      currentStepConfig?.page === 'video' &&
      currentPage === 'dashboard' &&
      !hasNavigatedRef.current &&
      teamId
    ) {
      // Find the recents folder for this team
      const recentsFolder = folders.find(
        (f) => f.teamId === teamId && f.name === 'Recents',
      );
      const folderId = recentsFolder?._id || 'recents';

      // Navigate to demo video
      hasNavigatedRef.current = true;
      navigate(`/library/${teamId}/${folderId}/${DEMO_VIDEO_ID}`);
    }
  }, [
    showTooltips,
    activeTooltipFeature,
    currentStepConfig,
    currentPage,
    tooltipStep,
    selectedTeam,
    folders,
    navigate,
  ]);

  // Handle button click - advances to next step and navigates/triggers action if needed
  const handleButtonClick = useCallback(() => {
    const currentStep = featureSteps[tooltipStep];
    const { teamId } = selectedTeam || {};

    if (currentStep?.navigateToDemo && teamId) {
      // For multi-step features, navigate to demo
      const recentsFolder = folders.find(
        (f) => f.teamId === teamId && f.name === 'Recents',
      );
      const folderId = recentsFolder?._id || 'recents';
      navigate(`/library/${teamId}/${folderId}/${DEMO_VIDEO_ID}`);

      // Advance tooltip after navigation
      if (advanceTooltip) {
        advanceTooltip();
      }
    } else {
      // For single-step features, trigger the action by clicking the target element's button
      const elementId = currentStep?.elementId;
      const targetElement = elementId
        ? document.getElementById(elementId)
        : null;

      if (targetElement) {
        // Find and click the button inside the target element
        const button = targetElement.querySelector('button');
        if (button) {
          // Dismiss tooltip first, then trigger the action
          if (advanceTooltip) {
            advanceTooltip();
          }
          // Small delay to let tooltip dismiss before action starts
          setTimeout(() => {
            button.click();
          }, 50);
        } else if (advanceTooltip) {
          // No button found, just advance the tooltip
          advanceTooltip();
        }
      } else if (advanceTooltip) {
        // Element not found, just advance the tooltip
        advanceTooltip();
      }
    }
  }, [
    advanceTooltip,
    featureSteps,
    tooltipStep,
    selectedTeam,
    folders,
    navigate,
  ]);

  // Handle X button click - cancels tooltip entirely
  const handleClose = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      if (cancelTooltip) {
        cancelTooltip();
      }
    },
    [cancelTooltip],
  );

  // Handle backdrop click - cancels tooltip entirely
  const handleBackdropClick = useCallback(() => {
    if (cancelTooltip) {
      cancelTooltip();
    }
  }, [cancelTooltip]);

  // Handle clicking on the highlighted element (e.g., demo video) - advances to next step
  useEffect(() => {
    if (!showTooltips || !activeTooltipFeature || !isOnCorrectPage) {
      return undefined;
    }

    const elementId = currentStepConfig?.elementId;
    if (!elementId) return undefined;

    const targetElement = document.getElementById(elementId);
    if (!targetElement) return undefined;

    const handleElementClick = (e: Event) => {
      const currentStep = featureSteps[tooltipStep];
      const { teamId } = selectedTeam || {};

      if (currentStep?.navigateToDemo) {
        // For multi-step features with navigation, prevent default and handle ourselves
        e.preventDefault();
        e.stopPropagation();

        if (teamId) {
          const recentsFolder = folders.find(
            (f) => f.teamId === teamId && f.name === 'Recents',
          );
          const folderId = recentsFolder?._id || 'recents';
          navigate(`/library/${teamId}/${folderId}/${DEMO_VIDEO_ID}`);
        }

        if (advanceTooltip) {
          advanceTooltip();
        }
      } else if (advanceTooltip) {
        // For single-step features, let the click propagate to trigger the element's action
        // Just dismiss the tooltip - don't prevent default so the button's onClick fires
        advanceTooltip();
      }
    };

    // Add click listener to the highlighted element
    targetElement.addEventListener('click', handleElementClick, true);

    return () => {
      targetElement.removeEventListener('click', handleElementClick, true);
    };
  }, [
    showTooltips,
    activeTooltipFeature,
    isOnCorrectPage,
    currentStepConfig,
    advanceTooltip,
    featureSteps,
    tooltipStep,
    selectedTeam,
    folders,
    navigate,
  ]);

  // Calculate position based on target element
  // This effect must be called unconditionally (before any returns)
  useEffect(() => {
    // Only run if we should show tooltips and we're on the correct page
    // IMPORTANT: Also check isObTooltipsVariant to prevent tooltips for deferred upgrade variant
    if (
      !isObTooltipsVariant ||
      !showTooltips ||
      !activeTooltipFeature ||
      !isOnCorrectPage
    ) {
      setPosition(null);
      setIsVisible(false);
      return undefined;
    }

    let retryCount = 0;
    const maxRetries = 30; // Retry up to 30 times
    let retryTimeoutId: ReturnType<typeof setTimeout>;
    let cleanedUp = false;

    const calculatePosition = () => {
      if (cleanedUp) return;

      const elementId = currentStepConfig?.elementId;
      const targetElement = elementId
        ? document.getElementById(elementId)
        : null;

      if (targetElement) {
        // Scroll element into view, centering it horizontally for better visibility
        targetElement.scrollIntoView({
          behavior: 'instant',
          block: 'nearest',
          inline: 'center',
        });

        // Use double RAF to ensure scroll and layout are fully complete
        requestAnimationFrame(() => {
          if (cleanedUp) return;
          requestAnimationFrame(() => {
            if (cleanedUp) return;

            const rect = targetElement.getBoundingClientRect();

            // Check if element has valid dimensions and is visible
            if (rect.width === 0 || rect.height === 0) {
              // Element exists but not visible yet, retry
              if (retryCount < maxRetries) {
                retryCount += 1;
                retryTimeoutId = setTimeout(calculatePosition, 100);
              }
              return;
            }

            const tooltipWidth = 320;
            const tooltipHeight = 150;
            const padding = 16;

            // Determine best position
            const viewportWidth = window.innerWidth;
            const viewportHeight = window.innerHeight;

            let newPosition: TooltipPosition;

            // Try to position below the element
            if (rect.bottom + tooltipHeight + padding < viewportHeight) {
              newPosition = {
                top: rect.bottom + padding,
                left: Math.max(
                  padding,
                  Math.min(
                    rect.left + rect.width / 2 - tooltipWidth / 2,
                    viewportWidth - tooltipWidth - padding,
                  ),
                ),
                arrowPosition: 'top',
              };
            }
            // Try to position above
            else if (rect.top - tooltipHeight - padding > 0) {
              newPosition = {
                top: rect.top - tooltipHeight - padding,
                left: Math.max(
                  padding,
                  Math.min(
                    rect.left + rect.width / 2 - tooltipWidth / 2,
                    viewportWidth - tooltipWidth - padding,
                  ),
                ),
                arrowPosition: 'bottom',
              };
            }
            // Try to position to the right
            else if (rect.right + tooltipWidth + padding < viewportWidth) {
              newPosition = {
                top: Math.max(
                  padding,
                  rect.top + rect.height / 2 - tooltipHeight / 2,
                ),
                left: rect.right + padding,
                arrowPosition: 'left',
              };
            }
            // Position to the left
            else {
              newPosition = {
                top: Math.max(
                  padding,
                  rect.top + rect.height / 2 - tooltipHeight / 2,
                ),
                left: rect.left - tooltipWidth - padding,
                arrowPosition: 'right',
              };
            }

            setPosition(newPosition);
            setIsVisible(true);

            // Add highlight to target element
            targetElement.classList.add('ob-tooltip-highlight');
          });
        });
      } else if (retryCount < maxRetries) {
        // Element not found, retry after a delay
        retryCount += 1;
        retryTimeoutId = setTimeout(calculatePosition, 100);
      } else {
        // Max retries reached, use fallback position
        const fallback = FALLBACK_POSITIONS[activeTooltipFeature] || {
          top: 200,
          left: 200,
        };
        setPosition({
          ...fallback,
          arrowPosition: 'left',
        });
        setIsVisible(true);
      }
    };

    // Start positioning immediately
    calculatePosition();

    // Recalculate on resize
    const handleResize = () => {
      retryCount = 0; // Reset retry count on resize
      calculatePosition();
    };
    window.addEventListener('resize', handleResize);

    return () => {
      cleanedUp = true;
      clearTimeout(retryTimeoutId);
      window.removeEventListener('resize', handleResize);

      // Remove highlight from all elements
      document.querySelectorAll('.ob-tooltip-highlight').forEach((el) => {
        el.classList.remove('ob-tooltip-highlight');
      });
    };
  }, [
    isObTooltipsVariant,
    showTooltips,
    activeTooltipFeature,
    isOnCorrectPage,
    currentStepConfig,
    tooltipStep,
  ]);

  // Don't render if conditions aren't met (after all hooks)
  // IMPORTANT: Only show tooltips for the tooltips variant, not for deferred upgrade variant
  if (
    !isObTooltipsVariant ||
    !showTooltips ||
    !activeTooltipFeature ||
    !isOnCorrectPage ||
    !position ||
    !message
  ) {
    return null;
  }

  const arrowClass = {
    top: 'before:absolute before:-top-2 before:left-1/2 before:-translate-x-1/2 before:border-8 before:border-transparent before:border-b-white dark:before:border-b-gray-800',
    bottom:
      'before:absolute before:-bottom-2 before:left-1/2 before:-translate-x-1/2 before:border-8 before:border-transparent before:border-t-white dark:before:border-t-gray-800',
    left: 'before:absolute before:top-1/2 before:-left-2 before:-translate-y-1/2 before:border-8 before:border-transparent before:border-r-white dark:before:border-r-gray-800',
    right:
      'before:absolute before:top-1/2 before:-right-2 before:-translate-y-1/2 before:border-8 before:border-transparent before:border-l-white dark:before:border-l-gray-800',
  };

  const tooltipContent = (
    <>
      {/* Backdrop */}
      <div
        role="button"
        tabIndex={0}
        className="fixed inset-0 bg-black/30 z-[9998] transition-opacity duration-300"
        style={{ opacity: isVisible ? 1 : 0 }}
        onClick={handleBackdropClick}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ' || e.key === 'Escape') {
            handleBackdropClick();
          }
        }}
        aria-label="Close tooltip"
      />

      {/* Tooltip */}
      <div
        ref={tooltipRef}
        className={`fixed z-[9999] w-80 bg-white dark:bg-gray-800 rounded-xl shadow-2xl p-4 transition-all duration-300 ${arrowClass[position.arrowPosition]}`}
        style={{
          top: position.top,
          left: position.left,
          opacity: isVisible ? 1 : 0,
          transform: isVisible ? 'scale(1)' : 'scale(0.95)',
        }}
        role="dialog"
        aria-labelledby="feature-tooltip-title"
        aria-describedby="feature-tooltip-description"
      >
        {/* Close button */}
        <button
          type="button"
          onClick={handleClose}
          className="absolute top-2 right-2 p-1 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
          aria-label="Close tooltip"
        >
          <X className="w-4 h-4 text-gray-400" />
        </button>

        {/* Content */}
        <div className="flex items-start gap-3">
          <div className="flex-shrink-0 w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center">
            <Sparkles className="w-5 h-5 text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <h3
              id="feature-tooltip-title"
              className="font-semibold text-gray-900 dark:text-gray-100 mb-1"
            >
              {message.title}
            </h3>
            <p
              id="feature-tooltip-description"
              className="text-sm text-gray-600 dark:text-gray-400 mb-3"
            >
              {message.description}
            </p>
            <Button
              size="sm"
              onClick={handleButtonClick}
              className="w-full bg-gradient-to-r from-blue-600 to-blue-500 hover:from-blue-700 hover:to-blue-600"
            >
              {message.buttonText || 'Try it now'}
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </div>

        {/* Pulse indicator */}
        <div className="absolute -top-1 -right-1 w-3 h-3">
          <span className="absolute inline-flex h-full w-full rounded-full bg-blue-400 opacity-75 animate-ping" />
          <span className="relative inline-flex rounded-full h-3 w-3 bg-blue-500" />
        </div>
      </div>

      {/* CSS for highlighted element */}
      <style>{`
        .ob-tooltip-highlight {
          position: relative;
          z-index: 9997 !important;
          box-shadow: 0 0 0 4px rgba(59, 130, 246, 0.5), 0 0 20px rgba(59, 130, 246, 0.3) !important;
          border-radius: 8px;
          animation: ob-pulse 2s infinite;
        }

        @keyframes ob-pulse {
          0%, 100% {
            box-shadow: 0 0 0 4px rgba(59, 130, 246, 0.5), 0 0 20px rgba(59, 130, 246, 0.3);
          }
          50% {
            box-shadow: 0 0 0 6px rgba(59, 130, 246, 0.3), 0 0 30px rgba(59, 130, 246, 0.2);
          }
        }
      `}</style>
    </>
  );

  return createPortal(tooltipContent, document.body);
};

export default FeatureTooltip;
